<script setup>
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import {
  EMPTY_VALUE,
  buildAnswerRows,
  contactLabel,
  formatDateTime,
  formatScore,
} from '../helper/surveyResponseHelper';
import {
  operationalProjectionSummaryLabel,
  operationalProjectionTone,
} from '../helper/operationalProjectionHelper';
import { useSurveyLabels } from '../composables/useSurveyLabels';

/**
 * Collected answers.
 *
 * Desktop gets a dense table; below `md` the same rows are rendered as cards so
 * the operator can triage from a phone without horizontal scrolling.
 */
defineProps({
  responses: { type: Array, default: () => [] },
  questions: { type: Array, default: () => [] },
});

const emit = defineEmits(['select']);

const { t, statusLabel, statusTone } = useSurveyLabels();

const answerLabels = () => ({
  yes: t('SURVEYS.PREVIEW.YES'),
  no: t('SURVEYS.PREVIEW.NO'),
});

const summaryFor = (response, questions) => {
  const rows = buildAnswerRows(
    questions,
    response.answers,
    answerLabels()
  ).filter(row => row.answered);
  if (!rows.length) return EMPTY_VALUE;
  return rows.map(row => `${row.label}: ${row.value}`).join(' · ');
};

const contactFor = response =>
  contactLabel(response, t('SURVEYS.RESPONSES.ANONYMOUS'));

const operationalProjectionFor = response => response.operational_projection;
</script>

<template>
  <div class="min-w-0">
    <div
      class="hidden overflow-x-auto rounded-lais border border-lais-border-soft bg-lais-bg-card md:block"
    >
      <table class="w-full min-w-[840px] border-collapse text-left">
        <thead>
          <tr class="border-b border-lais-border-soft bg-lais-bg-soft">
            <th
              class="px-4 py-3 text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-muted"
            >
              {{ t('SURVEYS.RESPONSES.TABLE.CONTACT') }}
            </th>
            <th
              class="px-4 py-3 text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-muted"
            >
              {{ t('SURVEYS.RESPONSES.TABLE.STATUS') }}
            </th>
            <th
              class="px-4 py-3 text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-muted"
            >
              {{ t('SURVEYS.RESPONSES.TABLE.SCORE') }}
            </th>
            <th
              class="px-4 py-3 text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-muted"
            >
              {{ t('SURVEYS.RESPONSES.TABLE.ANSWERS') }}
            </th>
            <th
              class="px-4 py-3 text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-muted"
            >
              {{ t('SURVEYS.RESPONSES.TABLE.RESPONDED_AT') }}
            </th>
            <th
              class="px-4 py-3 text-lais-label-xs font-semibold uppercase tracking-[0.04em] text-lais-text-muted"
            >
              {{ t('SURVEYS.RESPONSES.TABLE.OPERATION') }}
            </th>
            <th class="w-16 px-4 py-3">
              <span class="sr-only">
                {{ t('SURVEYS.RESPONSES.TABLE.ACTIONS') }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="response in responses"
            :key="response.id"
            class="border-b border-lais-border-soft last:border-0 transition hover:bg-lais-primary-50/30"
          >
            <td class="max-w-0 px-4 py-3">
              <span
                class="block truncate text-lais-body-sm font-medium text-lais-text"
              >
                {{ contactFor(response) }}
              </span>
            </td>
            <td class="px-4 py-3">
              <LaisStatusBadge
                :label="statusLabel(response.status)"
                :tone="statusTone(response.status)"
                size="sm"
                dot
              />
            </td>
            <td
              class="px-4 py-3 text-lais-body-sm font-semibold tabular-nums text-lais-text"
            >
              {{ formatScore(response.score) }}
            </td>
            <td class="max-w-0 px-4 py-3">
              <span
                class="block truncate text-lais-body-sm text-lais-text-body"
              >
                {{ summaryFor(response, questions) }}
              </span>
            </td>
            <td
              class="whitespace-nowrap px-4 py-3 text-lais-body-sm text-lais-text-body"
            >
              {{ formatDateTime(response.responded_at || response.sent_at) }}
            </td>
            <td class="px-4 py-3">
              <LaisStatusBadge
                v-if="operationalProjectionFor(response)"
                :label="
                  operationalProjectionSummaryLabel(
                    operationalProjectionFor(response),
                    t
                  )
                "
                :tone="
                  operationalProjectionTone(
                    operationalProjectionFor(response).status
                  )
                "
                size="sm"
              />
              <span v-else class="text-lais-body-sm text-lais-text-muted">
                {{ EMPTY_VALUE }}
              </span>
            </td>
            <td class="px-4 py-3 text-right">
              <button
                type="button"
                class="inline-flex h-8 items-center gap-1.5 rounded-lais border border-lais-border bg-white px-2.5 text-[12px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus"
                @click="emit('select', response)"
              >
                {{ t('SURVEYS.RESPONSES.TABLE.VIEW') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ul role="list" class="flex flex-col gap-3 md:hidden">
      <li v-for="response in responses" :key="`card-${response.id}`">
        <button
          type="button"
          class="flex w-full flex-col gap-2 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 text-left shadow-lais-card transition hover:border-lais-primary/50 focus-visible:outline-none focus-visible:shadow-lais-focus"
          @click="emit('select', response)"
        >
          <div class="flex min-w-0 items-center justify-between gap-2">
            <span
              class="min-w-0 truncate text-lais-body-md font-semibold text-lais-text"
            >
              {{ contactFor(response) }}
            </span>
            <LaisStatusBadge
              :label="statusLabel(response.status)"
              :tone="statusTone(response.status)"
              size="sm"
              dot
            />
          </div>
          <p class="line-clamp-2 text-lais-body-sm text-lais-text-body">
            {{ summaryFor(response, questions) }}
          </p>
          <div
            v-if="operationalProjectionFor(response)"
            class="flex items-center justify-between gap-3 rounded-lais-sm bg-lais-bg-soft px-3 py-2"
          >
            <span
              class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
            >
              {{ t('SURVEYS.RESPONSES.TABLE.OPERATION') }}
            </span>
            <LaisStatusBadge
              :label="
                operationalProjectionSummaryLabel(
                  operationalProjectionFor(response),
                  t
                )
              "
              :tone="
                operationalProjectionTone(
                  operationalProjectionFor(response).status
                )
              "
              size="sm"
            />
          </div>
          <div
            class="flex items-center justify-between gap-2 text-lais-label-xs text-lais-text-muted"
          >
            <span>{{
              formatDateTime(response.responded_at || response.sent_at)
            }}</span>
            <span class="font-semibold tabular-nums text-lais-text">
              {{ formatScore(response.score) }}
            </span>
          </div>
        </button>
      </li>
    </ul>
  </div>
</template>
